import React, { useCallback, useEffect, useMemo } from 'react'
import { Dropdown, makeStyles, mergeClasses, Option, tokens } from '@fluentui/react-components'
import { useSearchParams } from 'react-router-dom'
import { t } from '../../i18n/i18n'
import { ActivityPeriodType, getPeriodOptions, isActivityPeriodType, isValidPeriodValue } from '../../helpers/calendarHelper'

export const PERIOD_TYPE_PARAM = 'periodType'
export const PERIOD_PARAM = 'period'

const useStyles = makeStyles({
   filterWrapper: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      '@media screen and (max-width: 1007px)': {
         gap: tokens.spacingHorizontalS,
      },
      '@media screen and (max-width: 640px)': {
         flexDirection: 'column',
         width: '100%',
      },
   },
   dropdown: {
      minWidth: '150px',
      '@media screen and (max-width: 1007px)': {
         width: '50%',
      },
      '@media screen and (max-width: 640px)': {
         width: '100%',
      },
   },
})

type ActivityPeriodFilterProps = {
   year: number
   className?: string
}

/**
 * Filters a list of activities by quarter, month or ISO week. The selection lives in the URL
 * (?periodType=quarter&period=Q3) so it survives a reload and can be linked to, following the same
 * pattern as CostFilters. Values stored in the URL are language independent - see getPeriodOptions.
 */
export const ActivityPeriodFilter = ({ year, className }: ActivityPeriodFilterProps) => {
   const classes = useStyles()
   const [searchParams, setSearchParams] = useSearchParams()
   const periodTypeParam = searchParams.get(PERIOD_TYPE_PARAM)
   const periodParam = searchParams.get(PERIOD_PARAM)

   const periodType: ActivityPeriodType | null = periodTypeParam && isActivityPeriodType(periodTypeParam) ? periodTypeParam : null
   const period = periodType && isValidPeriodValue(periodType, periodParam, year) ? periodParam : null

   const setParams = useCallback(
      (entries: [string, string | null][]) => {
         entries.forEach(([key, value]) => {
            if (value) {
               searchParams.set(key, value)
            } else {
               searchParams.delete(key)
            }
         })
         setSearchParams(searchParams)
      },
      [searchParams, setSearchParams]
   )

   // Drop values a hand-edited or stale URL may carry, so the URL always reflects what is shown.
   useEffect(() => {
      const invalidType = Boolean(periodTypeParam) && !periodType
      const invalidPeriod = Boolean(periodParam) && !period
      if (invalidType || invalidPeriod) {
         setParams([
            [PERIOD_TYPE_PARAM, invalidType ? null : periodTypeParam],
            [PERIOD_PARAM, invalidPeriod ? null : periodParam],
         ])
      }
   }, [periodTypeParam, periodParam, periodType, period, setParams])

   const periodTypeOptions = useMemo(
      () => [
         { value: 'quarter', text: t('Quarter') },
         { value: 'month', text: t('Month') },
         { value: 'week', text: t('Week') },
      ],
      []
   )
   const periodOptions = useMemo(() => (periodType ? getPeriodOptions(periodType, year) : []), [periodType, year])

   const selectedPeriodTypeText = periodTypeOptions.find((x) => x.value === periodType)?.text ?? null
   const selectedPeriodText = periodOptions.find((x) => x.value === period)?.text ?? null

   return (
      <div className={mergeClasses(classes.filterWrapper, className ?? '')}>
         <Dropdown
            aria-label={t('FilterByPeriodType')}
            className={classes.dropdown}
            placeholder={t('FilterByPeriodType')}
            value={selectedPeriodTypeText}
            selectedOptions={periodType ? [periodType] : []}
            // Switching type always clears the value - "Q3" means nothing once the type is Week.
            onOptionSelect={(e, data) =>
               setParams([
                  [PERIOD_TYPE_PARAM, data.optionValue ?? null],
                  [PERIOD_PARAM, null],
               ])
            }
            clearable
         >
            {periodTypeOptions.map((x) => (
               <Option key={x.value} value={x.value}>
                  {x.text}
               </Option>
            ))}
         </Dropdown>
         <Dropdown
            aria-label={t('FilterByPeriod')}
            className={classes.dropdown}
            placeholder={t('FilterByPeriod')}
            value={selectedPeriodText}
            selectedOptions={period ? [period] : []}
            onOptionSelect={(e, data) => setParams([[PERIOD_PARAM, data.optionValue ?? null]])}
            clearable
            disabled={!periodType}
         >
            {periodOptions.map((x) => (
               <Option key={x.value} value={x.value}>
                  {x.text}
               </Option>
            ))}
         </Dropdown>
      </div>
   )
}
